import { useState, useEffect, useCallback, useRef } from 'react';
import { differenceInSeconds, parseISO } from 'date-fns';
import { SessionMetadata } from '../types';

export enum TimeoutType {
  IDLE = 'IDLE',
  ABSOLUTE = 'ABSOLUTE',
  NONE = 'NONE'
}

interface UseSessionTimeoutProps {
  sessionMetadata: SessionMetadata | null | undefined;
  onIdleExpire: () => void;
  onAbsoluteExpire: () => void;
  warningThresholdMinutes?: number; // Default 2 minutes
}

export const useSessionTimeout = ({
  sessionMetadata,
  onIdleExpire,
  onAbsoluteExpire,
  warningThresholdMinutes = 2
}: UseSessionTimeoutProps) => {
  // State
  const [lastActivity, setLastActivity] = useState<Date>(new Date());
  const [warningType, setWarningType] = useState<TimeoutType>(TimeoutType.NONE);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const timeLeftRef = useRef(timeLeft);
  useEffect(() => {
    timeLeftRef.current = timeLeft;
  }, [timeLeft]);

  // Refs to avoid dependency cycles in interval
  const onIdleExpireRef = useRef(onIdleExpire);
  const onAbsoluteExpireRef = useRef(onAbsoluteExpire);
  /** While > 0, idle expiry must not fire (e.g. refresh-session in flight after "Stay signed in"). */
  const idleExtensionInFlightRef = useRef(0);
  /** Snapshot of countdown when extension starts — keeps UI from ticking during the request. */
  const frozenIdleDisplaySecondsRef = useRef(0);

  // Update refs
  useEffect(() => {
    onIdleExpireRef.current = onIdleExpire;
    onAbsoluteExpireRef.current = onAbsoluteExpire;
  }, [onIdleExpire, onAbsoluteExpire]);

  const beginIdleExtension = useCallback(() => {
    idleExtensionInFlightRef.current += 1;
    if (idleExtensionInFlightRef.current === 1) {
      frozenIdleDisplaySecondsRef.current = Math.max(0, timeLeftRef.current);
    }
  }, []);

  const endIdleExtension = useCallback(() => {
    idleExtensionInFlightRef.current = Math.max(0, idleExtensionInFlightRef.current - 1);
  }, []);

  // Sync last activity from server session data when it changes (e.g. on refresh)
  useEffect(() => {
    if (sessionMetadata?.lastAccessedAt) {
      setLastActivity(parseISO(sessionMetadata.lastAccessedAt));
    }
  }, [sessionMetadata?.lastAccessedAt]);

  // Reset warning state when session changes (new login)
  // This prevents ghost dialog on re-login
  useEffect(() => {
    if (sessionMetadata?.absoluteExpiresAt) {
      setWarningType(TimeoutType.NONE);
      setTimeLeft(0);
    }
  }, [sessionMetadata?.absoluteExpiresAt]);

  // Reset idle timer (local interaction)
  const registerActivity = useCallback(() => {
    // Only update if we're not in a warning state (don't auto-clear warning)
    // And don't update if already expired
    setWarningType(current => {
      // If we are already in warning state, we DO NOT auto-reset
      // User must explicitly click "Stay Signed In" in the modal
      if (current !== TimeoutType.NONE) return current;

      setLastActivity(new Date());
      return TimeoutType.NONE;
    });
  }, []);

  // Main check interval (runs every second)
  useEffect(() => {
    if (!sessionMetadata) return;

    const intervalId = setInterval(() => {
      const now = new Date();
      const { idleTimeoutMinutes, absoluteExpiresAt } = sessionMetadata;
      const absoluteExpiry = parseISO(absoluteExpiresAt);

      // 1. Check Absolute Timeout (Priority 1)
      // -------------------------------------
      const secondsUntilAbsolute = differenceInSeconds(absoluteExpiry, now);

      // Logged out if past absolute expiry
      if (secondsUntilAbsolute <= 0) {
        clearInterval(intervalId);
        onAbsoluteExpireRef.current();
        return;
      }

      // Warning if within threshold
      const absoluteWarningThreshold = warningThresholdMinutes * 60;
      if (secondsUntilAbsolute <= absoluteWarningThreshold) {
        setWarningType(TimeoutType.ABSOLUTE);
        setTimeLeft(secondsUntilAbsolute);
        return;
      }

      // 2. Check Idle Timeout (Priority 2)
      // ----------------------------------
      // Calculate when idle timeout will happen based on last activity
      const idleExpiry = new Date(lastActivity.getTime() + idleTimeoutMinutes * 60 * 1000);
      const secondsUntilIdle = differenceInSeconds(idleExpiry, now);

      // Logged out if idle (unless extension request is in flight)
      if (secondsUntilIdle <= 0) {
        if (idleExtensionInFlightRef.current > 0) {
          setWarningType(current =>
            current === TimeoutType.ABSOLUTE ? current : TimeoutType.IDLE
          );
          setTimeLeft(frozenIdleDisplaySecondsRef.current);
          return;
        }
        clearInterval(intervalId);
        onIdleExpireRef.current();
        return;
      }

      // Warning if within threshold
      const idleWarningThreshold = warningThresholdMinutes * 60;
      if (secondsUntilIdle <= idleWarningThreshold) {
        // Only set idle warning if we aren't already absolute warning
        // (Absolute takes precedence as it's harder constraint)
        setWarningType(current => (current === TimeoutType.ABSOLUTE ? current : TimeoutType.IDLE));
        if (idleExtensionInFlightRef.current > 0) {
          setTimeLeft(frozenIdleDisplaySecondsRef.current);
        } else {
          setTimeLeft(secondsUntilIdle);
        }
        return;
      }

      // No warnings
      setWarningType(TimeoutType.NONE);
      setTimeLeft(0);

    }, 1000);

    return () => clearInterval(intervalId);
  }, [sessionMetadata, lastActivity, warningThresholdMinutes]);

  return {
    warningType,
    timeLeft,
    registerActivity,
    lastActivity,
    beginIdleExtension,
    endIdleExtension
  };
};
